import React, { useState, useRef } from 'react';
import { Button, TextInput, Spinner } from 'flowbite-react';
import showToast from '../CustomToast/toastHelper';
import client from '../../api/client';
import CustomModal from '../Modal/CustomModal';
import SecurityQuestions from '../SecurityQuestions/SecurityQuestions';
import ConfirmPinModal from '../ConfirmPinModal/ConfirmPinModal';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

interface CreatePinModalProps {
    onSuccess: () => void,
    message: string,
    buttonText: string
    otp?: string
    type: string,
    close: () => void,
    topMessage?: string,
    downMessage?: string,
    securityQuestionPayload?: any,
    closeAllModals: () => void,
    oldPin?:string
}
const CreatePinModal = ({ onSuccess,oldPin, topMessage, downMessage, message, buttonText, otp, close, type, securityQuestionPayload, closeAllModals }: CreatePinModalProps) => {

    const [pin, setPin] = useState<string[]>(['', '', '', '']);
    const [confirmPin, setConfirmPin] = useState<string[]>(['', '', '', '']);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [openSecurityModal, setOpenSecurityModal] = useState<boolean>(false)
    const [openConfirmPinModal, setOpenConfirmPinModal] = useState<boolean>(false)
    const [showOtp, setShowOtp] = useState<boolean>(false)

    const pinRefs = useRef<(HTMLInputElement | null)[]>([]);
    const confirmPinRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number,
        type: 'pin' | 'confirmPin'
    ) => {
        const { value } = e.target;
        if (value.length > 1 || isNaN(Number(value))) return; // Only accept numbers

        const newPinArray = type === 'pin' ? [...pin] : [...confirmPin];
        newPinArray[index] = value;

        type === 'pin' ? setPin(newPinArray) : setConfirmPin(newPinArray);

        // Move to next input
        if (value && index < 3) {
            (type === 'pin' ? pinRefs : confirmPinRefs).current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number,
        type: 'pin' | 'confirmPin'
    ) => {
        if (e.key === 'Backspace' && index > 0 && !(type === 'pin' ? pin : confirmPin)[index]) {
            (type === 'pin' ? pinRefs : confirmPinRefs).current[index - 1]?.focus();
        }
    };

    const handleSubmit = async () => {
        if (pin.join('') !== confirmPin.join('')) {
            setError("PINs do not match!");
            return;
        }

        const payload = {

        }
        setLoading(true)
        try {
            let url;
            let payload;
            if (type == 'reset') {
                url = "/subsidiary/dashboard/pin/reset/complete"
                payload = {
                    otp,
                    pin: pin.join(''),
                    confirmPin: confirmPin.join(''),
                    securityQuestion: securityQuestionPayload
                }
                const response = await client.post(url, payload);
                const message = response?.data?.message;
                showToast(message, "success")
                setPin([])
                setConfirmPin([])
                onSuccess()
            }
            else if (type == 'create') {
                url = "/subsidiary/dashboard/pin/create"
                // payload = {
                //     otp,
                //     pin: pin.join(''),
                //     confirmPin: confirmPin.join(''),
                // }
                setOpenSecurityModal(true)
            }
            else {
                url = '/subsidiary/dashboard/pin/change'
                payload = {
                    oldPin:oldPin,
                    newPin: pin.join(''),
                    confirmNewPin: pin.join('')
                }
                console.log(payload);
                
                const response = await client.post(url, payload);
                const message = response?.data?.message;
                showToast(message, "success")
                onSuccess()
            }

        } catch (err: any) {

            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                console.error('error getting data', err);
                const message = err?.response?.data?.message;
                showToast(message, "error")
            }
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center p-6 w-full max-w-sm mx-auto rounded-lg">
            <p className='text-center text-md font-semibold mb-2'>{message}</p>
            {/* PIN Input */}
            <div>
                <p className='text-xs mb-2'>{topMessage ?? 'Enter new PIN'}</p>
            </div>
            <div className='flex gap-3 mb-4'>
                {pin.map((digit, index) => (
                    <TextInput
                        key={`pin-${index}`}
                        type={showOtp ? 'text' : 'password'}
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(e, index, 'pin')}
                        onKeyDown={(e) => handleKeyDown(e, index, 'pin')}
                        ref={(el) => (pinRefs.current[index] = el)}
                        className="w-12 text-center text-xl font-bold border border-gray-300 rounded-lg focus:border-blue-500"
                    />
                ))}
            </div>

            {/* Confirm PIN Input */}
            <div>
                <p className='text-xs mb-2'>{downMessage ?? 'Confirm new PIN'}</p>
            </div>
            <div className='flex gap-3 mb-2'>
                {confirmPin.map((digit, index) => (
                    <TextInput
                        key={`confirm-${index}`}
                        type={showOtp ? 'text' : 'password'}
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(e, index, 'confirmPin')}
                        onKeyDown={(e) => handleKeyDown(e, index, 'confirmPin')}
                        ref={(el) => (confirmPinRefs.current[index] = el)}
                        className="w-12 text-center text-xl font-bold border border-gray-300 rounded-lg focus:border-blue-500"
                    />
                ))}
            </div>

            {/* Toggle Visibility */}
            <div className="flex justify-center items-center mb-2">
                <button type="button" onClick={() => setShowOtp(!showOtp)} className="p-2 w-8 h-8 flex items-center justify-center rounded-md">
                    {showOtp ? <FaEyeSlash className="w-6 h-6 text-gray-600" /> : <FaEye className="w-6 h-6 text-gray-600" />}
                </button>
            </div>

            {/* Error Message */}
            {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}

            {/* Submit Button */}
            {/* <Button onClick={handleSubmit} className='w-full bg-custom-button-green text-white font-semibold py-2 rounded-lg'>
                {loading ? <Spinner size='sm' /> : buttonText}
            </Button> */}
            <div className="flex gap-4">
                <Button onClick={() => close()} className="bg-white text-black hover:text-white border-2 border-gray-300 hover:bg-gray-100 px-4 py-2 rounded-md">
                    Close
                </Button>
                <Button disabled={pin.join('').length != 4 || confirmPin.join('').length != 4} onClick={() =>  handleSubmit() } className="bg-custom-button-green text-white px-4 py-2 rounded-md hover:bg-green-600 transition">
                    {loading ? <Spinner size='sm' /> : buttonText}
                </Button>
            </div>
            <CustomModal size='lg' dismissible show={openSecurityModal} icon='' onClose={() => setOpenSecurityModal(false)}>
                <SecurityQuestions closeAllModals={() => closeAllModals()} otp={otp} pin={pin.join('')} confirmPin={confirmPin.join('')} close={() => setOpenSecurityModal(false)} />
            </CustomModal>
            
        </div>
    );
};

export default CreatePinModal;
